import {computed, inject, Injectable, resource, ResourceLoaderParams, ResourceStatus, signal} from '@angular/core';
import {
    AccountData,
    AccountDataUpdateLog,
    ClassificationTierApi,
    ClassificationTierTarget,
    Kpi,
    KPIApi,
    Portfolio,
    PortfolioApi,
    PortfolioPeriodEnum,
    Segment,
    SegmentAccountApi,
    SegmentApi,
    SegmentClassificationTierTargetApi,
    SegmentStatusEnum, SegmentTargetApi, Target, TieringStats
} from '../../api-client-kt/src';
import {
    CollectionView,
    NotifyCollectionChangedAction,
    NotifyCollectionChangedEventArgs,
    SortDescription
} from '@mescius/wijmo';
import {trimIndent} from '../lib/strings';
import {RouteReuseStrategy} from '@angular/router';
import {AppRouteReuseStrategy} from '../app-routing-reuse-strategy';
import {Alerts, DialogButton} from '../lib/alerts';
import {DataMap} from '@mescius/wijmo.grid';
import {IsoCurrencyCodesDatamap} from './iso-currencies';
import {IsoCuntryCodesDatamap} from './iso-countries';
import {
    DataMapLeadFollow,
    DataMapMethodology,
    DataMapRenewable,
    DataMapRenewalStatus,
    DataMapTieringFlag,
    DataMapYesNo
} from './data-maps';
import {NullablePrimitive, Stdlib} from '../lib/stdlib';
import {ApiConfiguration} from '../restclient/api.config';
import {IntervalWorker} from '../lib/interval-worker';
import {ArrayUtils} from '../lib/arrays';
import {DataMapKeyType} from './data-dictionary';
import {PortfolioSegmentClassificationScheme} from './portfolio-segment-classification-scheme';
import {AppConstants} from '../app.constants';
import {PortfolioSegmentWorkbook} from './portfolio-segment-workbook';
import {PortfolioDataContext, SegmentWithPermissions} from './portfolio-data-context.service';
import {AccountSegmentStatisticsCalculator} from './portfolio-segment-statistics';
import {KeyValueAndParamsService} from '../service/key-value-and-params.service';
import {
    AccountDataNotifyCollectionChangedEventArgs
} from '../components/account-comments-editor/account-comments-editor.component';
import {AuthService} from '../security/auth.service';
import {AppDataFormulaService} from './app-data-formula.service';

/**
 * Represents the context for managing and interacting with portfolio segments within an application.
 * It encapsulates portfolio and segment data, provides resources for interacting with these entities,
 * and supports advanced features such as formula evaluation, validation, and resource loading.
 * This context is the 'data store' of the application's current segment. It's not a global variable
 * (or service) because it can be attached to a component, and the application might have multiple
 * concurrently active segments.
 *
 * Key Features:
 * - Signals and resource bindings to manage reactive state changes and asynchronous updates for portfolio and segment data.
 * - Resource loaders for fetching and maintaining data such as portfolios, account collections, and segment KPIs.
 * - Support for debounced formula evaluation and validation.
 * - Automatic detection of changes in account data and segment states with background worker integration.
 * - Computed properties to track the loading status of all resources.
 * - Controlled activation of portfolio and segment contexts.
 * - Integration with Wijmo's `CollectionView` to facilitate data collection management.
 * - Support for classification tiers and their associated targets within a segment.
 *
 * Background Features:
 * - Integrates an interval worker for periodically checking account updates in the background.
 * - Ensures debounce mechanisms for remote evaluation of account formulas to avoid excessive requests during user interactions.
 */
@Injectable()
export class PortfolioSegmentDataContext {
    readonly pfContext = inject(PortfolioDataContext);

    // primary keys for portfolio and segment from which the data is loaded via resource loaders
    public readonly portfolioId = signal<string | undefined>(undefined);
    public readonly segmentId = signal<string | undefined>(undefined);

    public readonly segmentStatus = signal<SegmentStatusEnum>(SegmentStatusEnum.Open);

    // the portfolio information
    public readonly portfolio = resource({
        params: () => this.portfolioId(),
        loader: ({params}) => this.fetchPortfolio(params)
    });

    // the accounts of the active segment: we store the accounts as a Wijmo CollectionView, because we need to
    // track changes to the accounts and to be able to update the formulas and validations accordingly.
    public readonly accountsCollectionView = resource({
        params: () => this.segmentId(),
        loader: ({params}) => this.fetchAccountData(params),
        defaultValue: new CollectionView<AccountData>([])
    });

    private readonly _accountsLoadedAt = signal<Date | undefined>(undefined);
    public readonly accountsLoadedAt = this._accountsLoadedAt.asReadonly();

    // a place to store the current sort description for the accounts collection view,
    // so that it can be applied after the accounts have been reloaded.
    private accountsCollectionViewSortDescriptions: SortDescription[] | undefined;

    public readonly accountsChangedInDbSinceLastLoad = resource({
        params: () => ({ segmentId: this.segmentId(), lastLoadedAt: this.accountsLoadedAt() }),
        loader: ({ params }) => this.loadAccountUpdateDetails(params.segmentId, params.lastLoadedAt!),
        defaultValue: []
    });

    public readonly activeSegment = resource({
        params: () => this.segmentId(),
        loader: ({ params }) => this.loadSegment(params)
    });

    public readonly segmentKpis = resource({
        params: () => this.segmentId(),
        loader: ({ params }) => this.loadSegmentKpis(params),
        defaultValue: []
    });

    public readonly classificationTierSchema = resource({
        params: this.segmentId,
        loader: this.classificationTiersLoader.bind(this),
        defaultValue: PortfolioSegmentClassificationScheme.EMPTY
    });
    private classificationTierSchemaIsDirty = false;

    private readonly _segmentParameters = signal<Record<string, NullablePrimitive>>({});
    public readonly segmentParameters = this._segmentParameters.asReadonly();

    private readonly _isDirty = signal(false);
    public readonly isDirty = this._isDirty.asReadonly();

    // application wide filter for the accounts that fall into this period (by policy expiry date)
    private  readonly _periodFilter = signal<PortfolioPeriodEnum|undefined|null>(undefined  );
    public readonly periodFilter = this._periodFilter.asReadonly();

    private formulaRequestCount$ = 0;

    allResourcesLoaded = computed(() => {
        // all resources loaded when they have either been 'resolved' or
        // programmatically assigned via .set(...), which is status 'local'
        return this.getLoadableResourcesStatus().every(s => s === 'resolved' || s === 'local');
    });

    // remote evaluation and validation of accounts as debounced resource loader
    // according to https://stackoverflow.com/questions/79321123/debouncing-a-search-term-when-using-the-new-angular-resource-api
    private readonly accountDataFormulaEvalSignal = signal<number | undefined>(undefined);
    readonly accountDataFormulaEvaluation = resource({
        params: () => this.accountDataFormulaEvalSignal(),
        loader: async (param) => {
            if (this.accountsCollectionView.value().sourceCollection.length === 0) {
                return undefined;
            } else {
                // debounce the evaluation request to make sure fast typing does not trigger too many requests
                await Stdlib.debounceWaitWithAbortSignal(100, param.abortSignal);
                return await this.wb.evaluateAccountFormulasAndChecks({ parameters: this.getParameters(), abortSignal: param.abortSignal });
            }
        }
    });

    private readonly appDataFormulaService = inject(AppDataFormulaService);

    // the portfolio segment workbook, which is kind of the analogue of the Excel workbook of the PIL files
    // it contains the formula-evaluated information about the accounts
    public readonly wb = new PortfolioSegmentWorkbook(this, this.appDataFormulaService);

    // pulse to check for account updates on the server from a background thread
    private accountUpdatesCheckInBackgroundWorker: IntervalWorker | undefined;
    private accountUpdatesCheckInBackgroundFrequencyMs= AppConstants.getChangeDetectionFrequency() ?? 10_000;

    // API references for the portfolio, segment, and KPI APIs and Angular base logic
    private readonly routeReuseStrategy = inject(RouteReuseStrategy) as AppRouteReuseStrategy;
    private readonly portfolioApi = inject(PortfolioApi);
    private readonly segmentApi = inject(SegmentApi);
    private readonly kpiApi = inject(KPIApi);
    private readonly segmentAccountApi  = inject(SegmentAccountApi);
    private readonly segmentTargetApi  = inject(SegmentTargetApi);
    private readonly classificationTierApi  = inject(ClassificationTierApi);
    private readonly segmentClassificationTierApi  = inject(SegmentClassificationTierTargetApi);
    private readonly keyValueParamsSvc = inject(KeyValueAndParamsService);

    constructor() {
    }

    /**
     * Retrieves the status of various loadable resources within the application.
     * Combines the status of active segments, account collections, segment KPIs,
     * and classification tier schema into a single array.
     *
     * Whenever more dependent resources are added to the portfolio segment data context,
     * their status must be included in this method to ensure that the overall loading state
     * is accurately represented.
     *
     * @return {Array} An array containing the status of the active segment,
     * accounts collection view, segment KPIs, and classification tier schema.
     */
    private getLoadableResourcesStatus(): Array<ResourceStatus> {
        return [this.activeSegment.status(), this.accountsCollectionView.status(), this.segmentKpis.status(), this.classificationTierSchema.status()];
    }

    async waitForResourcesToLoad(segmentId?: string) {
        if (segmentId && segmentId != this.segmentId()) {
            try {
                await Stdlib.waitUntil(() => this.segmentId() === segmentId);
            } catch (e) {
                console.error('Timeout waiting for segmentId to match:', e);
            }
        }
        await Stdlib.signalPredicateToPromise(this.allResourcesLoaded, s => s);
    }

    setParameter(name: string, value: NullablePrimitive) {
        const newParameters = { ... this._segmentParameters(), [name]: value };
        this._segmentParameters.set(newParameters);
    }

    getParameter(name: string): NullablePrimitive {
        return this._segmentParameters()[name];
    }

    getParameters(): Record<string, NullablePrimitive> {
        return this._segmentParameters();
    }

    /**
     * Retrieves the array of account data from the collection view's source collection.
     * This is the account data, but without the change tracker functionality of Wijmo's CollectionView.'
     */
    getAccountData(): AccountData[] {
        return this.accountsCollectionView.value().sourceCollection;
    }

    getAccountDataInFilterPeriod(): AccountData[] {
        const uwYear = this.portfolio.value()?.uwyear;
        return this.getAccountData()
            .filter(AccountSegmentStatisticsCalculator.accountPeriodFilter(uwYear, this.periodFilter()));
    }

    getAccountDataInFilterPeriodAndTouchFormulaEval(): AccountData[] {
        const evaluated = this.accountDataFormulaEvaluation.value();
        return this.getAccountDataInFilterPeriod();
    }

    /**
     * Sets the value of the period filter that is shared across the Angular components.
     */
    togglePeriodFilter(period: PortfolioPeriodEnum | undefined): PortfolioPeriodEnum | undefined | null {
        if (this._periodFilter() === period) this._periodFilter.set(undefined);
        else this._periodFilter.set(period);
        return this._periodFilter();
    }

    /**
     *  Ensures that the account data formula evaluation resource is triggered.
     *  Required for effect() calls that want to be invoked after the formula evaluation.
     */
    ensureEffectOnFormulaEvaluation() {
        this.accountDataFormulaEvaluation.value();
    }

    async activatePortfolioById(portfolioId: string) {
        console.log( `========= ACTIVATE PORTFOLIO ${portfolioId}`);
        console.log('Activating portfolio', portfolioId);
        const currentPortfolioId = this.portfolioId();

        if (PortfolioDataContext.isNullPortfolioId(currentPortfolioId)) {
            this.portfolioId.set(portfolioId);
        } else if (currentPortfolioId !== portfolioId) {
            console.error(`Cannot activate portfolio ${portfolioId}, current portfolio is ${currentPortfolioId}`);
        }
    }

    async activatePortfolioSegmentByIds(portfolioId: string, segmentId: string) {
        console.log( `========= ACTIVATE SEGMENT ${segmentId}`);
        const currentPortfolioId = this.portfolioId();
        const currentSegmentId = this.segmentId();

        // ignore if the same portfolio and segment are already active
        if (portfolioId === currentPortfolioId && segmentId === currentSegmentId) {
            console.log('Reactivating the same portfolio/segment combination -> Ignore', portfolioId, segmentId);
            return;
        }

        console.log('Activating portfolio segment', portfolioId, segmentId);
        if (PortfolioDataContext.isNullPortfolioId(currentPortfolioId)) {
            await this.activatePortfolioById(portfolioId);
        } else if (currentPortfolioId !== portfolioId) {
            console.error(`Cannot activate segment ${segmentId} for portfolio ${portfolioId}, current portfolio is ${currentPortfolioId}`);
            throw new Error(`Cannot activate segment ${segmentId} for portfolio ${portfolioId}, current portfolio is ${currentPortfolioId}`);
        }

        if (currentSegmentId) {
            await this.deactivateSegment();
        }

        this.segmentId.set(segmentId);

        // get the segment parameters from PocketBase - temporarily until we have a stable
        // data model for the portfolio segment parameters and can load them via the Segment API
        const params = await this.keyValueParamsSvc.getSegmentParams(segmentId);
        this._segmentParameters.set(params);

        // Like in an Excel workbook, we evaluate the formulas and validations after the segment has been activated.
        // However, we don't consider the updated calculated fields as 'changed' to allow for read-only opening
        // of the workbook in Excel without suggesting a save (-> call markPristine()).
        await this.waitForResourcesToLoad();
        await Stdlib.signalPredicateToPromise(this.pfContext.portfolio.status, s => s === 'resolved' || s === 'local');
        // ... when the initial filter should be the current's exercise period, uncomment the below
        // this._periodFilter.set(this.pfContext.portfolio.value()?.period);

        const formulaEvalResult = await this.wb.evaluateAccountFormulasAndChecks({ parameters: this.getParameters() });
        this.markPristine();
        this.accountDataFormulaEvaluation.set(formulaEvalResult);

        // finally schedule the background worker to check for account updates
        this.accountUpdatesCheckInBackgroundWorker = IntervalWorker.schedule(
            `checkAccountUpdatesInBackgroundWorker-${segmentId}`,
            this.accountUpdatesCheckInBackgroundFrequencyMs,
            this.checkAccountUpdatesInBackground.bind(this), true
        );
        console.log( `========= ACTIVATE SEGMENT ${segmentId} ENDED`);
    }

    private async classificationTiersLoader(param: ResourceLoaderParams<string|undefined>): Promise<PortfolioSegmentClassificationScheme> {
        const segmentId = param.params;
        if (!segmentId) return PortfolioSegmentClassificationScheme.EMPTY;

        const tiers = await this.classificationTierApi.getAllTiers({ id: this.portfolioId()! });
        const tierTargets = await this.segmentClassificationTierApi.getAllTierRanges({ id: segmentId });

        this.classificationTierSchemaIsDirty = false;
        return new PortfolioSegmentClassificationScheme(tiers, tierTargets);
    }

    updateClassificationTierSchema(tierTargets: ClassificationTierTarget[]) {
        const tiers = this.classificationTierSchema.value()!.tiers;
        const schema = new PortfolioSegmentClassificationScheme(tiers, tierTargets);
        this.classificationTierSchema.set(schema);
        this.classificationTierSchemaIsDirty = true;
        this._isDirty.set(true);
    }

    private async fetchAccountData0(segmentId: string | undefined): Promise<AccountData[]> {
        if (!segmentId) {
            return [];
        } else {
            const accounts = await this.segmentAccountApi.getAccountsBySegment({ id: segmentId});
            const canonicalSortFn = (a: AccountData, b: AccountData) => {
                const gnwpDiff = (b.gnwpEur ?? 0) - (a.gnwpEur ?? 0);
                if (gnwpDiff !== 0) {
                    return gnwpDiff;
                }
                // Secondary sort by account.account (string) when gnwpEur is equal
                const aAccount = a.account ?? '';
                const bAccount = b.account ?? '';
                return aAccount.localeCompare(bAccount);
            };
            return accounts.sort(canonicalSortFn) as any as AccountData[];
        }
    }

    async deactivatePortfolioAndSegment() {
        await this.deactivateSegment();
        this.portfolioId.set(undefined);
        console.log('Deactivated portfolio and segment');
    }

    async deactivateSegment() {
        const currentSegmentId = this.segmentId();
        if (currentSegmentId) {
            this.segmentId.set(undefined);
            this._isDirty.set(false);
            this.classificationTierSchema.set(PortfolioSegmentClassificationScheme.EMPTY);
            this.accountUpdatesCheckInBackgroundWorker?.stop();
            this.accountUpdatesCheckInBackgroundWorker = undefined;
            console.log(`Deactivated segment ${currentSegmentId}`);

            // Delay the route reuse strategy discard to ensure the view is updated before reusing routes.
            // Otherwise, it may happen that the current view is detached and thus its state stored
            // even if this is exactly what we want to avoid.
            setTimeout(() => {
                console.log(`Disabling route reuse for segment ${currentSegmentId}`);
                this.routeReuseStrategy.discardRoutesForSegment(currentSegmentId);
            }, 1000);
        } else {
            console.warn('No active segment to deactivate');
        }
    }

    private async fetchPortfolio(portfolioId: string | undefined): Promise<Portfolio | undefined> {
        if (PortfolioDataContext.isNullPortfolioId(portfolioId)) {
            return undefined;
        } else {
            return await this.portfolioApi.getPortfolio({ id: portfolioId });
        }
    }

    private async fetchAccounts(segmentId: string | undefined): Promise<AccountData[]> {
        this.accountsChangedInDbSinceLastLoad.set([]);
        const data = await this.fetchAccountData0(segmentId);

        data.forEach((account: any, idx: number) => {
            this.patchAccountData(account, idx);
        });

        return data;
    }

    private async fetchAccountData(segmentId: string | undefined): Promise<CollectionView<AccountData>> {
        const data = await this.fetchAccounts(segmentId);
        const adv = new CollectionView(data);
        adv.trackChanges = true;

        adv.collectionChanged.addHandler((v, e) => {
            if (e?.action != null && e?.action != NotifyCollectionChangedAction.Reset) this._isDirty.set(true);
        });

        const maxChangedAt = ArrayUtils.maxDate(data, it => it.updatedAt);
        const loadedAt = Stdlib.maxDate(maxChangedAt, new Date(Date.now() + 5_000));
        console.log(`Max changed at = ${maxChangedAt}, new loaded at check = ${loadedAt}`);

        this._accountsLoadedAt.set(loadedAt);
        this._isDirty.set(false);

        if (this.accountsCollectionViewSortDescriptions) {
            adv.sortDescriptions.push(...this.accountsCollectionViewSortDescriptions);
            this.accountsCollectionViewSortDescriptions = undefined;
        }

        return adv;
    }

    private patchAccountData(account: AccountData, idx: number) {
        (account as any).row = idx + 1;
        account.keyAccount = account.keyAccount === true;
        account.renewableFlag = account.renewableFlag === true || account.renewableFlag == undefined;
        account.uwy = account.uwy ? account.uwy : account.inceptionDate?.getFullYear();

        // metadata checks and commments are critical - the MUST be arrays
        // however, the server allows for any kind of JSON text in these fields, so we need to patch them in case they are not arrays
        if (account.metadataChecks != null && !Array.isArray(account.metadataChecks)) account.metadataChecks = [];
        if (account.metadataComments != null && !Array.isArray(account.metadataComments)) account.metadataComments = [];
    }

    async getDataMap(dataMapKey: DataMapKeyType): Promise<DataMap> {
        switch (dataMapKey) {
            case 'currency': return IsoCurrencyCodesDatamap;
            case 'country': return IsoCuntryCodesDatamap;
            case 'YesNo': return DataMapYesNo;
            case 'RenewableFlag': return DataMapRenewable;
            case 'RenewalStatus': return DataMapRenewalStatus;
            case 'TieringFlag': return DataMapTieringFlag;
            case 'Methodology': return DataMapMethodology;
            case 'LeadFollow': return DataMapLeadFollow;
            default: throw new Error(`Unknown data map key ${dataMapKey}`);
        }
    }

    async save(saveSegmentParameters: boolean) {
        const cv = this.accountsCollectionView.value();
        if (!cv) return;

        const changes = trimIndent`
            <ul>
                <li>${cv.itemsEdited.length ?? 0} changed accounts</li>
                <li>${cv.itemsAdded.length ?? 0} new lines</li>
                <li>${cv.itemsRemoved.length ?? 0} deleted lines</li>
            </ul>
        `;
        const btn = await Alerts.alertPopup({
            header: 'Save Accounts',
            bodyHtml: `The following changes have been done: ${changes} Would you like to save the changes to the accounts?`,
            buttons: [DialogButton.Cancel, DialogButton.No, DialogButton.Yes],
            height: '250px',
            width: '350px',
        });

        if (btn === DialogButton.Yes) {
            const segmentId = this.segmentId()!;
            const deletedIds = cv.itemsRemoved.map(a => a.id!) ?? [];
            const createdIds = cv.itemsAdded.map(a => a.id!) ?? [];
            const createdIdsSet = new Set(createdIds);
            const notCreated = cv.sourceCollection.filter(a => !createdIdsSet.has(a.id!));
            const calculated = PortfolioSegmentWorkbook.mapCalculatedFields(notCreated);

            const modifyResult = await this.segmentAccountApi.modifyAccounts({ id: segmentId!, modificationRequest: {
                    create: cv.itemsAdded as AccountData[],
                    update: cv.itemsEdited as AccountData[],
                    _delete: deletedIds,
                    calculated: calculated
                }
            });

            if (saveSegmentParameters) {
                const saveRequests: Promise<any>[] = [];

                if (this.classificationTierSchemaIsDirty) {
                    saveRequests.push(
                        this.segmentClassificationTierApi.updateTierRanges({
                            id: segmentId,
                            classificationTierTarget: this.classificationTierSchema.value()!.tierTargets
                        })
                    );
                }

                const targets: Target[] = this.wb.summaryToTargets(this.classificationTierSchema.value().tierTargets);
                saveRequests.push(this.segmentTargetApi.updateTargets({id: segmentId, target: targets}));

                const tieringStats: TieringStats[] = this.wb.tieringStats();
                saveRequests.push(this.segmentTargetApi.updateTieringStats({
                    id: segmentId,
                    tieringStats: tieringStats
                }));

                // and the segment parameters via PocketBase (temporarily)
                saveRequests.push(this.keyValueParamsSvc.storeSegmentParams(segmentId, this._segmentParameters()));

                // now wait for all the save requests to complete
                await Promise.all(saveRequests);
            }

            // buffer the current sort descriptions to be applied after the save,
            // that way, the UI remains stable and accounts are listed in the same
            // order as they were before the save.
            this.accountsCollectionViewSortDescriptions = cv.sortDescriptions;

            // then finally reload to have the current list synced with the server side
            this.accountsCollectionView.reload();
            console.log('Changes saved successfully');
        } else {
            console.log('Save changes cancelled');
        }
    }

    async discard() {
        const btn = await Alerts.alertPopup({
            header: 'Discard Changes',
            bodyHtml: `You have unsaved changes. Are you sure you want to discard all changes?`,
            buttons: [DialogButton.Cancel, DialogButton.No, DialogButton.Yes]
        });

        if (btn === DialogButton.Yes) {
            const portfolioId = this.portfolioId()!;
            const segmentId = this.segmentId()!;
            await this.deactivateSegment();
            await this.activatePortfolioSegmentByIds(portfolioId, segmentId);
            console.log('Discarded changes');
        } else {
            console.log('Discard changes cancelled');
        }
    }

    updateFormulasAndValidations() {
        this.accountDataFormulaEvalSignal.set(this.formulaRequestCount$++);
    }

    checkAccountUpdatesInBackground() {
        this.accountsChangedInDbSinceLastLoad.reload();
    }

    setAccountUpdatesCheckInBackgroundFrequency(intervalMs: number) {
        this.accountUpdatesCheckInBackgroundWorker?.setInterval(intervalMs);
    }

    private async loadAccountUpdateDetails(segmentId: string | undefined, from: Date | undefined): Promise<AccountDataUpdateLog[]> {
        if (!segmentId || !from) return [];
        const oldValue = this.accountsChangedInDbSinceLastLoad.value() ?? [];
        const newValue = await this.segmentAccountApi.getAccountUpdatesBySegment(
            { id: segmentId, from: from },
            ApiConfiguration.markAsSilentRequest
        );
        return this.hasUpdateListNewerItems(newValue, oldValue) ? newValue : oldValue;
    }

    private hasUpdateListNewerItems(newValue: AccountDataUpdateLog[], oldValue: AccountDataUpdateLog[] | undefined) {
        if (!oldValue || oldValue.length === 0) {
            return newValue.length > 0;
        }
        const maxNewUpdatedAt = ArrayUtils.maxDate(newValue, it => it.updatedAt) ?? new Date(0);
        const maxOldUpdatedAt = ArrayUtils.maxDate(oldValue, it => it.updatedAt) ?? new Date(0);
        return maxNewUpdatedAt > maxOldUpdatedAt;
    }

    async mergeServerSideAccountUpdates() {
        // This method merges server-side account updates into the current accounts collection view.
        const cv = this.accountsCollectionView.value();
        const changed = this.accountsChangedInDbSinceLastLoad.value();
        const changedIds = new Set<string>(changed.map(c => c.accountId!));
        if (!cv || changedIds.size === 0)  return;

        const accountsById = new Map<string, AccountData>(cv.sourceCollection.map(a => [a.id!, a]));
        const localIds = new Set<string>(accountsById.keys());
        const locallyModifiedIds = new Set<string>();
        const locallyRemovedIds = new Set<string>();

        cv.itemsEdited?.forEach(a => locallyModifiedIds.add(a.id!));
        cv.itemsRemoved?.forEach(a => locallyRemovedIds.add(a.id!));


        const serverSideAccounts = await this.segmentAccountApi.getAccountsBySegment({ id: this.segmentId()! });
        const serverSideIds = new Set<string>(serverSideAccounts.map(a => a.id!));
        const missingOnServerIds = Stdlib.difference(localIds, serverSideIds)

        cv.deferUpdate(() => {
            serverSideAccounts
                .forEach(change => {
                    const changeId = change.id!;
                    const existingAccount = accountsById.get(changeId);

                    if (existingAccount) {
                        // always update the version, because it may have been incremented
                        // due to formula evaluation or other non-editing changes
                        existingAccount.version = change.version;

                        // take over all the new changes from the server for the ones
                        // changed on the server - even the ones changed locally
                        // the app has shown a warning before the user actively 'synced'
                        // and that must be good enough - local changes will be discarded
                        if (changedIds.has(changeId)) {
                            Object.assign(existingAccount, change);
                            if (locallyModifiedIds.has(changeId)) {
                                cv.itemsEdited.remove(existingAccount);
                            }
                        }
                    } else if (locallyRemovedIds.has(changeId)) {
                        // the account was removed locally, so we do not want to overwrite it
                    } else {
                        // this is a new account that was added on the server, so we add it to the collection view
                        cv.sourceCollection.push(change);
                    }
                });

            // remove accounts that are not present on the server anymore
            for (let i = cv.sourceCollection.length - 1; i >= 0; i--) {
                const account = cv.sourceCollection[i];
                if (missingOnServerIds.has(account.id!)) {
                    if (locallyModifiedIds.has(account.id!)) {
                        console.log(`Account ${account.id} was modified locally, but not on the server. Removing it from the collection view.`);
                    }
                    cv.sourceCollection.splice(i, 1);
                }
            }
        });
        this._isDirty.set(true);
        this._accountsLoadedAt.set(new Date());
        console.log(`Merged ${serverSideAccounts.length} server-side account updates`);

        this.updateFormulasAndValidations();

        // send the application-specific event to notify the UI that the collection view has changed
        // (for example, needed for the comments pane to be updated)
        cv.onCollectionChanged(new AccountDataNotifyCollectionChangedEventArgs(NotifyCollectionChangedAction.Change));
    }

    private async loadSegment(segmentId: string | undefined) {
        if (!segmentId) return undefined;

        const segment = await this.segmentApi.getSegment({ id: segmentId });
        const kpis = await this.kpiApi.getAllKpis({ id: segmentId });
        const segmentWithNames = this.pfContext.toSegmentWithName({ segment, hasPermission: true, accountCount: -1, statsByStatusAndPeriod: [] });
        segmentWithNames.kpis = kpis;
        this.segmentStatus.set(segment.status);
        return segmentWithNames as SegmentWithPermissions;
    }

    private async loadSegmentKpis(segmentId: string | undefined): Promise<Kpi[]> {
        if (!segmentId) return [];

        const kpis = await this.kpiApi.getAllKpis({ id: segmentId });
        return kpis;
    }

    markPristine() {
        this._isDirty.set(false);
    }

    updateClassificationTierSchemaValue(tier: string, attribute: string, value: number | undefined | null) {
        this.classificationTierSchema.value()?.updateTargetValue(tier, attribute, value);
        this._isDirty.set(true);
    }

    async updateSegmentStatus(status: SegmentStatusEnum, statusChangeComment: string): Promise<Segment> {
        const segment = await this.segmentApi.updateStatus({
            id: this.segmentId()!,
            statusRequest: { segmentStatus: status, comment: statusChangeComment }
        });
        this.segmentStatus.set(status);
        this.activeSegment.reload();
        return segment;
    }
}
